import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../hooks/useAuth';
import { sounds } from '../utils/sounds';
import { track } from '../utils/analytics';
import Toast from '../components/Toast';
import AdBanner from '../components/AdBanner';

// Show an ad every N verdicts
const AD_EVERY = 8;

export default function Feed() {
  const { user, profile } = useAuth();

  const [queue,      setQueue]      = useState([]);
  const [current,    setCurrent]    = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [voting,     setVoting]     = useState(false);
  const [toast,      setToast]      = useState(null);
  const [exhausted,  setExhausted]  = useState(false);
  const [seen,       setSeen]       = useState(new Set());
  const [streak,     setStreak]     = useState(0);
  const [totalGiven, setTotalGiven] = useState(0);
  const [activeBtn,  setActiveBtn]  = useState(null);  // 'valid' | 'mid' | null
  const [flashColor, setFlashColor] = useState(null);  // 'valid' | 'mid' | null
  const [showAd,     setShowAd]     = useState(false);

  // Use a ref for submitVerdict so keyboard handler always has latest version
  const submitRef = useRef(null);

  const fetchNext = useCallback(async () => {
    // Get user's own DOB to calculate age range
    const { data: myProfile } = await supabase
      .from('profiles')
      .select('date_of_birth')
      .eq('id', user.id)
      .single();

    // Get all profiles this user has already rated
    const { data: ratedData } = await supabase
      .from('ratings')
      .select('rated_user_id')
      .eq('rater_user_id', user.id);

    const alreadyRated = new Set((ratedData || []).map(r => r.rated_user_id));
    alreadyRated.add(user.id);

    // Build age filter if user has DOB set
    let query = supabase
      .from('profiles')
      .select('id, display_name, profile_photo_url, current_score, total_ratings_received, date_of_birth')
      .eq('photo_approved', true)
      .neq('id', user.id)
      .order('created_at', { ascending: false })
      .limit(200);

    // Apply age filter — within 16 years either side, min age 18
    if (myProfile?.date_of_birth) {
      const myAge = Math.floor(
        (new Date() - new Date(myProfile.date_of_birth)) / (365.25 * 24 * 60 * 60 * 1000)
      );
      const minAge = Math.max(18, myAge - 16);
      const maxAge = myAge + 16;

      const maxDob = new Date();
      maxDob.setFullYear(maxDob.getFullYear() - minAge);
      const minDob = new Date();
      minDob.setFullYear(minDob.getFullYear() - maxAge - 1);

      query = query
        .lte('date_of_birth', maxDob.toISOString().split('T')[0])
        .gte('date_of_birth', minDob.toISOString().split('T')[0]);
    }

    const { data, error } = await query;
    if (error || !data?.length) return [];

    // Filter out already-rated profiles in JS
    return data.filter(p => !alreadyRated.has(p.id));
  }, [user.id]);

  useEffect(() => {
    async function init() {
      setLoading(true);
      const profiles = await fetchNext();
      if (profiles.length === 0) {
        setExhausted(true);
      } else {
        const [first, ...rest] = profiles;
        setCurrent(first);
        setQueue(rest);
        setSeen(new Set(profiles.map(p => p.id)));
      }
      setLoading(false);
    }
    init();
  }, [fetchNext]);

  // Keyboard shortcuts — arrow keys trigger verdicts via ref
  useEffect(() => {
    function handleKey(e) {
      if (e.key === 'ArrowLeft'  && submitRef.current) submitRef.current('valid');
      if (e.key === 'ArrowRight' && submitRef.current) submitRef.current('mid');
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  async function submitVerdict(verdict) {
    if (!current || voting) return;
    setVoting(true);

    // Step 1 — show overlay and button active state on CURRENT photo
    setActiveBtn(verdict);
    setFlashColor(verdict);

    // Step 2 — wait for overlay to be visible, then advance
    await new Promise(resolve => setTimeout(resolve, 650));

    setActiveBtn(null);
    setFlashColor(null);

    const rated = current;
    const next  = queue[0] || null;
    setCurrent(next);
    setQueue(q => q.slice(1));
    setSeen(s => new Set([...s, rated.id]));

    const newStreak  = verdict === 'valid' ? streak + 1 : 0;
    const newTotal   = totalGiven + 1;
    setStreak(newStreak);
    setTotalGiven(newTotal);

    // Show ad every AD_EVERY verdicts
    if (newTotal % AD_EVERY === 0) setShowAd(true);

    if (newStreak === 5) sounds.streak();
    else if (verdict === 'valid') sounds.valid();
    else sounds.mid();

    if ([10, 25, 50, 100].includes(newTotal)) {
      setTimeout(() => sounds.milestone(), 400);
    }

    setToast({ message: verdict === 'valid' ? 'Valid.' : 'Mid.', type: verdict });

    try {
      const { error } = await supabase.from('ratings').insert({
        rater_user_id:       user.id,
        rated_user_id:       rated.id,
        verdict,
        rater_score_at_time: profile?.current_score ?? null,
      });
      if (error) throw error;
      track('verdict_submitted', { verdict });
    } catch (err) {
      console.error('Rating insert failed:', err.message);
    }

    if (queue.length <= 2) {
      const newProfiles = await fetchNext();
      const freshProfiles = newProfiles.filter(p => !seen.has(p.id));
      if (freshProfiles.length > 0) {
        setQueue(q => [...q, ...freshProfiles]);
        setSeen(s => new Set([...s, ...freshProfiles.map(p => p.id)]));
        if (!next) setCurrent(freshProfiles[0]);
      } else if (!next) {
        setExhausted(true);
      }
    }

    setVoting(false);
  }

  // Keep ref in sync with latest submitVerdict
  submitRef.current = submitVerdict;

  if (loading) {
    return (
      <div className="page" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ fontFamily: 'var(--font-heading)', fontSize: '1.4rem', color: 'var(--text-muted)' }}>
          Loading your feed...
        </p>
      </div>
    );
  }

  if (exhausted || !current) {
    return (
      <div className="page" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <div className="container">
          <div className="feed-empty card text-center">
            <h2 style={{ fontFamily: 'var(--font-heading)', color: 'var(--text-muted)' }}>
              You have judged everyone.
            </h2>
            <p className="mt-16">
              New profiles appear as people join.<br />
              Come back later, or check your own score.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page" style={{ alignItems: 'center', justifyContent: 'center' }}>
      <div className="container">

        {profile && !profile.photo_approved && profile.profile_photo_url && (
          <div style={{
            background: 'rgba(255,214,10,0.06)',
            border: '1px solid rgba(255,214,10,0.2)',
            borderRadius: 'var(--radius)',
            padding: '10px 16px',
            marginBottom: 20,
            fontSize: '0.8rem',
            color: 'var(--gold)',
            textAlign: 'center',
          }}>
            Your photo is under review. Rating others while you wait.
          </div>
        )}

        <div className="profile-photo-card" key={current.id} style={{ position: 'relative' }}>
          <img
            src={current.profile_photo_url}
            alt={`${current.display_name}'s profile`}
            loading="eager"
          />

          {/* Tinder-style verdict stamp overlay */}
          {flashColor && (
            <div style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: flashColor === 'valid'
                ? 'rgba(74, 222, 128, 0.18)'
                : 'rgba(168, 85, 247, 0.18)',
              borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0',
              animation: 'stampIn 0.15s ease',
              zIndex: 10,
            }}>
              <div style={{
                fontFamily: 'var(--font-heading)',
                fontSize: 'clamp(3rem, 12vw, 5rem)',
                fontWeight: 700,
                letterSpacing: '0.08em',
                color: flashColor === 'valid' ? 'var(--valid-color)' : 'var(--mid-color)',
                border: `4px solid ${flashColor === 'valid' ? 'var(--valid-color)' : 'var(--mid-color)'}`,
                padding: '8px 24px',
                borderRadius: 8,
                transform: flashColor === 'valid' ? 'rotate(-12deg)' : 'rotate(12deg)',
                textShadow: flashColor === 'valid'
                  ? '0 0 30px rgba(74,222,128,0.6)'
                  : '0 0 30px rgba(168,85,247,0.6)',
                boxShadow: flashColor === 'valid'
                  ? '0 0 20px rgba(74,222,128,0.3)'
                  : '0 0 20px rgba(168,85,247,0.3)',
                opacity: 0.95,
              }}>
                {flashColor === 'valid' ? 'VALID' : 'MID'}
              </div>
            </div>
          )}

          <div className="card-meta">
            <span className="card-name">{current.display_name}</span>
            <span className="card-visitors">
              {current.total_ratings_received || 0} people formed an opinion
            </span>
          </div>
        </div>

        {/* Verdict buttons with arrow indicators and active states */}
        <div className="verdict-buttons">
          <button
            className="btn-valid"
            onClick={() => submitVerdict('valid')}
            disabled={voting}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              transform: activeBtn === 'valid' ? 'scale(0.95)' : 'scale(1)',
              background: activeBtn === 'valid' ? 'rgba(74, 222, 128, 0.2)' : 'transparent',
              transition: 'transform 0.1s ease, background 0.1s ease',
            }}
          >
            <span style={{ fontSize: '1.2rem', opacity: 0.7 }}>←</span>
            Valid
          </button>
          <button
            className="btn-mid"
            onClick={() => submitVerdict('mid')}
            disabled={voting}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              transform: activeBtn === 'mid' ? 'scale(0.95)' : 'scale(1)',
              background: activeBtn === 'mid' ? 'rgba(168, 85, 247, 0.2)' : 'transparent',
              transition: 'transform 0.1s ease, background 0.1s ease',
            }}
          >
            Mid
            <span style={{ fontSize: '1.2rem', opacity: 0.7 }}>→</span>
          </button>
        </div>

        <p className="text-center text-muted mt-16" style={{ fontSize: '0.72rem', letterSpacing: '0.06em' }}>
          Use arrow keys to rate
        </p>

        {/* Ad unit — shown every AD_EVERY verdicts */}
        {showAd && (
          <div className="mt-24">
            <AdBanner
              slot={process.env.REACT_APP_ADSENSE_SLOT_FEED || ''}
              format="rectangle"
              style={{ marginTop: 8 }}
            />
            <button
              className="btn-ghost mt-8"
              onClick={() => setShowAd(false)}
              style={{ width: '100%', fontSize: '0.75rem' }}
            >
              Continue rating
            </button>
          </div>
        )}
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
